/**
 * Okta authn/API response shapes. Lives in a side-effect–free module so
 * `resolveAuthnMfaDisplayEmail` (and other utils) can import it without
 * creating a require cycle: useOkta → AuthService → resolveAuthnMfaDisplayEmail.
 */

// Okta Types
export type OktaUser = {
  login: string
  email?: string
  id?: string
  profile?: {
    firstName?: string
    lastName?: string
    displayName?: string
    email?: string
  }
}

export type OktaMfaFactor = {
  id: string
  factorType: string
  provider: string
  vendorName?: string
  profile?: {
    phoneNumber?: string
    credentialId?: string
    [key: string]: any
  }
  _links?: {
    verify?: {
      href: string
      hints?: {
        allow: string[]
      }
    }
    resend?:
      | Array<{
          href: string
          name: string
          hints?: {
            allow: string[]
          }
        }>
      | {
          href: string
          hints?: {
            allow: string[]
          }
        }
    [key: string]: any
  }
  [key: string]: any
}

export type OktaPasswordPolicy = {
  minLength: number
  minLowerCase: number
  minUpperCase: number
  minNumber: number
  minSymbol: number
  excludeUsername: boolean
}

export type OktaAuthnResponse = {
  status: string
  stateToken?: string
  sessionToken?: string
  user?: OktaUser
  expiresAt?: string
  recoveryType?: string
  passwordPolicy?: OktaPasswordPolicy
  resetPasswordUrl?: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _embedded?: {
    user?: any
    policy?: {
      complexity?: OktaPasswordPolicy
      age?: {
        minAgeMinutes: number
        historyCount: number
      }
    }
    factors?: OktaMfaFactor[]
    stateToken?: string
  }
  _links?: {
    next?: {
      name: string
      href: string
      hints?: {
        allow: string[]
      }
    }
    cancel?: {
      href: string
      hints?: {
        allow: string[]
      }
    }
    [key: string]: any
  }
  errorCode?: string
  errorSummary?: string
  // Additional nested response structures from Okta API
  data?: {
    stateToken?: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _embedded?: {
      factors?: OktaMfaFactor[]
      user?: any
    }
  }
  response?: {
    stateToken?: string
  }
}

export type OktaPasswordResetResponse = {
  status: string
  stateToken?: string
  sessionToken?: string
  user?: OktaUser
  expiresAt?: string
  _embedded?: {
    user?: OktaUser
    factors?: any[]
  }
  _links?: {
    cancel?: {
      href: string
    }
  }
}

export type OktaTokenResponse = {
  access_token: string
  token_type: string
  expires_in: number
  scope: string
  refresh_token?: string
  id_token?: string
}

export enum OktaResponseStatus {
  RECOVERY_CHALLENGE = 'RECOVERY_CHALLENGE',
}
